import { hasAgentCursorAttentionIntent } from "@/components/cursors/agent-cursor-intent";
import { resolveCursorConversationId } from "@/desktop/cursor-conversation-id";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { cursorTargetIsOnDisplay } from "@/lib/agent-annotation";
import { presentedWindowBox } from "@/lib/focus/focus-windows";
import { wallVisibleHere } from "@/lib/wall-visibility";
import {
  conversationFrameForRun,
  cursorRunIdentity,
  type VisibleConversation,
} from "@/lib/run-surface-owner";
import type { AgentCursor } from "@/stores/canvas-store";
import {
  useCompanionStore,
  type ActiveExecution,
  type AgentSession,
} from "@/stores/companion-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { isWallActive } from "@/stores/workspace-view-store";

export interface CanvasCursorVisibilityInput {
  contextualExecutionIds: readonly string[];
  projectId?: string;
  spaceId?: string;
  visibleConversations: readonly VisibleConversation[];
  dockEnabled: boolean;
  activeGuideId: string | null;
  executions: Map<string, ActiveExecution>;
  agentSessions: Record<string, AgentSession>;
}

/**
 * Whether the canvas cursor layer renders this cursor, and the visible
 * conversation frame that shows its run.
 */
export function canvasCursorVisibility(
  cursor: AgentCursor,
  input: CanvasCursorVisibilityInput,
): { visible: boolean; owningConversationFrameId: string | null } {
  if (
    (cursor.executionId &&
      input.contextualExecutionIds.includes(cursor.executionId)) ||
    (cursor.projectId
      ? cursor.projectId !== input.projectId
      : cursor.spaceId !== input.spaceId)
  ) {
    return { visible: false, owningConversationFrameId: null };
  }
  const owningConversationFrameId = conversationFrameForRun(
    cursorRunIdentity(
      cursor,
      resolveCursorConversationId(
        cursor,
        input.executions,
        input.agentSessions,
      ),
    ),
    input.visibleConversations,
  );
  const hasAttentionIntent =
    hasAgentCursorAttentionIntent(cursor) ||
    (!!input.activeGuideId &&
      cursor.presentationDeliveryId === input.activeGuideId);
  // The dock, or a visible conversation frame showing this run, owns
  // ordinary status; only a point, drawing, desktop run or guide stays.
  return {
    visible:
      hasAttentionIntent ||
      (!input.dockEnabled && owningConversationFrameId === null),
    owningConversationFrameId,
  };
}

function onActiveDisplay(
  displayId: number | null,
  activeDisplayId: number | null,
): boolean {
  return (
    activeDisplayId === null ||
    displayId === null ||
    activeDisplayId === displayId
  );
}

/**
 * The display whose window shows frame `frameId` as a window: its floating
 * window's, or the presentation's (null: any display). Undefined when the
 * frame shows as a wall card or canvas node.
 */
export function frameWindowDisplayId(
  frameId: string,
): number | null | undefined {
  const floating = useFloatingFrameStore.getState().frames[frameId];
  if (floating) return floating.displayId;
  const { presentation } = usePresentationStore.getState();
  return presentedWindowBox(presentation, frameId)
    ? (presentation?.displayId ?? null)
    : undefined;
}

/** `frameWindowDisplayId`, re-rendering when it changes. */
export function useFrameWindowDisplayId(
  frameId: string | null | undefined,
): number | null | undefined {
  const floatingDisplayId = useFloatingFrameStore((state) =>
    frameId ? state.frames[frameId]?.displayId : undefined,
  );
  const presentedDisplayId = usePresentationStore((state) =>
    frameId && presentedWindowBox(state.presentation, frameId)
      ? (state.presentation?.displayId ?? null)
      : undefined,
  );
  return floatingDisplayId ?? presentedDisplayId;
}

/**
 * One display draws a frame-anchored cursor (Part 06 decision 10): the one
 * showing the frame's focus or floating window; else, for a wall card, the
 * active display while its wall shows. The old canvas spans the displays,
 * so each draws the node's cursor where it shows the node.
 */
function frameCursorPaintsOnDisplay(
  frameId: string,
  displayId: number | null,
  activeDisplayId: number | null,
): boolean {
  const windowDisplayId = frameWindowDisplayId(frameId);
  if (windowDisplayId !== undefined) {
    return (
      windowDisplayId === null ||
      displayId === null ||
      windowDisplayId === displayId
    );
  }
  if (!isWallActive()) return true;
  return onActiveDisplay(displayId, activeDisplayId) && wallVisibleHere();
}

/**
 * Whether this window paints a rendered canvas cursor. A screen target
 * belongs to its display; a frame target to the display that shows the
 * frame; an idle cursor away from any frame belongs to the window under the
 * user's pointer.
 */
export function canvasCursorPaintsOnDisplay(
  cursor: AgentCursor,
  displayId: number | null,
  activeDisplayId: number | null,
): boolean {
  if (!cursorTargetIsOnDisplay(cursor, displayId)) return false;
  if (cursor.pointTarget) return true;
  if (cursor.targetFrameId) {
    return frameCursorPaintsOnDisplay(
      cursor.targetFrameId,
      displayId,
      activeDisplayId,
    );
  }
  return onActiveDisplay(displayId, activeDisplayId);
}

/** Whether the cursor's run is still going, so the stale sweep keeps it. */
export function isActiveCursorExecution(cursor: AgentCursor): boolean {
  const { executions, agentSessions } = useCompanionStore.getState();
  const isActiveStatus = (status: string | undefined) =>
    status === "running" || status === "waiting_approval";

  if (isActiveStatus(executions.get(cursor.agentId)?.status)) return true;

  const bareName = (cursor.name ?? "").split(" · ")[0]?.toLowerCase();
  const linkedSession = bareName
    ? agentSessions[`${bareName}:${cursor.spaceId}`]
    : undefined;
  if (
    isActiveStatus(executions.get(linkedSession?.executionId ?? "")?.status)
  ) {
    return true;
  }

  for (const execution of executions.values()) {
    if (!isActiveStatus(execution.status)) continue;
    if (execution.spaceId && execution.spaceId !== cursor.spaceId) continue;
    const executionName = (execution.agentName ?? execution.agentKey ?? "")
      .split(" · ")[0]
      ?.toLowerCase();
    if (bareName && executionName === bareName) return true;
  }

  return false;
}
